import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    Container,
    Paper,
    Group,
    Stack,
    Title,
    Text,
    Tabs,
    Select,
} from "@mantine/core";
import HeroSectionV2 from "../../components/HeroSectionV2";
import Billede1 from "../../Images/Billlede-1.png";

import TransportRouteRoutesTab from "./components/TransportRouteRoutesTab";
import TransportRouteStopsTab from "./components/TransportRouteStopsTab";
import TransportRouteLocationsTab from "./components/TransportRouteLocationsTab";
import TransportRouteDeparturesTab from "./components/TransportRouteDeparturesTab";
import TransportRouteDepartureStopsTab from "./components/TransportRouteDepartureStopsTab";
import type {
    TransportRouteDto,
    TransportRouteStopDto,
    TransportRouteLocationDto,
    TransportDepartureDto,
    TransportDepartureStopDto,
} from "./types";

const apiUrl = process.env.REACT_APP_API_URL;

type TransportAdminEventDto = {
    eventId: number;
    name: string;
};

export default function TransportRouteAdminPage() {
    const [events, setEvents] = useState<TransportAdminEventDto[]>([]);
    const [eventId, setEventId] = useState<number | null>(null);

    const [locations, setLocations] = useState<TransportRouteLocationDto[]>([]);
    const [routes, setRoutes] = useState<TransportRouteDto[]>([]);
    const [routeStops, setRouteStops] = useState<TransportRouteStopDto[]>([]);
    const [selectedRouteId, setSelectedRouteId] = useState<number | null>(null);

    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState<string | null>("routes");

    const [departures, setDepartures] = useState<TransportDepartureDto[]>([]);

    const [departureStops, setDepartureStops] = useState<TransportDepartureStopDto[]>([]);
    const [selectedDepartureId, setSelectedDepartureId] = useState<number | null>(null);
    const [availableRouteStops, setAvailableRouteStops] = useState<TransportRouteStopDto[]>([]);


    useEffect(() => {
        (async () => {
            const res = await axios.get(`${apiUrl}/api/transport/admin/events`);
            setEvents(res.data ?? []);

            if (res.data?.length) {
                setEventId(res.data[0].eventId);
            }
        })();
    }, []);

    const eventOptions = useMemo(
        () => events.map((e) => ({ value: String(e.eventId), label: e.name })),
        [events]
    );

    const loadLocations = async (eid: number) => {
        const res = await axios.get(`${apiUrl}/api/transport-route/admin/locations`, {
            params: { eventId: eid },
        });

        setLocations(res.data ?? []);
    };

    const loadRoutes = async (eid: number) => {
        const res = await axios.get(`${apiUrl}/api/transport-route/admin/routes`, {
            params: { eventId: eid },
        });

        const data = res.data ?? [];
        setRoutes(data);

        if (data.length > 0) {
            setSelectedRouteId((prev) => {
                if (prev && data.some((x: TransportRouteDto) => x.transportRouteID === prev)) {
                    return prev;
                }
                return data[0].transportRouteID;
            });
        } else {
            setSelectedRouteId(null);
        }
    };

    const loadRouteStops = async (routeId: number) => {
        const res = await axios.get(`${apiUrl}/api/transport-route/admin/route-stops`, {
            params: { routeId },
        });

        setRouteStops(res.data ?? []);
    };

    const loadDepartures = async (eid: number) => {
        const res = await axios.get(`${apiUrl}/api/transport-route/admin/departures`, {
            params: { eventId: eid },
        });

        setDepartures(res.data ?? []);
    };

    const loadDepartureStops = async (departureId: number) => {
        const res = await axios.get(`${apiUrl}/api/transport-route/admin/departure-stops`, {
            params: { departureId },
        });

        setDepartureStops(res.data ?? []);
    };

    const loadRouteStopsForDeparture = async (departureId: number) => {
        const res = await axios.get(`${apiUrl}/api/transport-route/admin/route-stops-for-departure`, {
            params: { departureId },
        });

        setAvailableRouteStops(res.data ?? []);
    };

    const refreshAll = async (eid: number) => {
        setLoading(true);
        try {
            await loadLocations(eid);
            await loadRoutes(eid);
            await loadDepartures(eid);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedDepartureId) {
            loadDepartureStops(selectedDepartureId);
            loadRouteStopsForDeparture(selectedDepartureId);
        } else {
            setDepartureStops([]);
            setAvailableRouteStops([]);
        }
    }, [selectedDepartureId]);

    useEffect(() => {
        if (eventId) {
            refreshAll(eventId);
        }
    }, [eventId]);

    useEffect(() => {
        if (selectedRouteId) {
            loadRouteStops(selectedRouteId);
        } else {
            setRouteStops([]);
        }
    }, [selectedRouteId]);

    const onTabChange = async (tab: string | null) => {
        setActiveTab(tab);

        if (!eventId) return;

        if (tab === "routes") {
            await loadRoutes(eventId);
        }

        if (tab === "routeStops" && selectedRouteId) {
            await loadRouteStops(selectedRouteId);
        }

        if (tab === "departures" && eventId) {
            await loadDepartures(eventId);
        }
        if (tab === "departureStops" && selectedDepartureId) {
            await loadDepartureStops(selectedDepartureId);
            await loadRouteStopsForDeparture(selectedDepartureId);
        }
    };

    return (
        <div>
            <HeroSectionV2
                titleEn="Transport Routes"
                titleDa="Transportruter"
                descriptionEn="Setup for route based transport"
                descriptionDa="Opsætning af rutebaseret transport"
                imageSrc={Billede1}
            />

            <Container size="xl" py="xl">
                <Paper withBorder p="lg" radius="md">
                    <Group justify="space-between" align="flex-end">
                        <Stack gap={2}>
                            <Title order={3}>Transport Route Admin</Title>
                            <Text c="dimmed" size="sm">
                                Opret ruter og stop til rutebaseret transport
                            </Text>
                        </Stack>

                        <Select
                            label="Vælg event"
                            data={eventOptions}
                            value={eventId ? String(eventId) : null}
                            onChange={(v) => {
                                setSelectedRouteId(null);
                                setEventId(v ? Number(v) : null);
                            }}
                            searchable
                            w={320}
                        />
                    </Group>
                </Paper>

                <Paper withBorder p="lg" radius="md" mt="md">
                    <Tabs value={activeTab} onChange={onTabChange} color="red">
                        <Tabs.List>
                            
                            <Tabs.Tab value="routes">Ruter</Tabs.Tab>
                            <Tabs.Tab value="locations">Locations</Tabs.Tab>
                            <Tabs.Tab value="routeStops">Route stops</Tabs.Tab>
                            
                            <Tabs.Tab value="departures">Afgange</Tabs.Tab>
                            <Tabs.Tab value="departureStops">Departure stops</Tabs.Tab>
                        </Tabs.List>

                        <Tabs.Panel value="locations" pt="md">
                            <TransportRouteLocationsTab
                                eventId={eventId}
                                locations={locations}
                                refreshLocations={async () => {
                                    if (eventId) {
                                        await loadLocations(eventId);
                                    }
                                }}
                            />
                        </Tabs.Panel>

                        <Tabs.Panel value="routes" pt="md">
                            <TransportRouteRoutesTab
                                eventId={eventId}
                                routes={routes}
                                refreshRoutes={async () => {
                                    if (eventId) {
                                        await loadRoutes(eventId);
                                    }
                                }}
                                selectedRouteId={selectedRouteId}
                                onRouteSelected={setSelectedRouteId}
                            />
                        </Tabs.Panel>

                        <Tabs.Panel value="routeStops" pt="md">
                            <TransportRouteStopsTab
                                selectedRouteId={selectedRouteId}
                                routeStops={routeStops}
                                locations={locations}
                                refreshRouteStops={async () => {
                                    if (selectedRouteId) {
                                        await loadRouteStops(selectedRouteId);
                                    }
                                }}
                            />
                        </Tabs.Panel>

                        <Tabs.Panel value="departures" pt="md">
                            <TransportRouteDeparturesTab
                                eventId={eventId}
                                routes={routes}
                                departures={departures}
                                refreshDepartures={async () => {
                                    if (eventId) {
                                        await loadDepartures(eventId);
                                    }
                                }}
                            />
                        </Tabs.Panel>
                        <Tabs.Panel value="departureStops" pt="md">
                            <TransportRouteDepartureStopsTab
                                departures={departures}
                                selectedDepartureId={selectedDepartureId}
                                setSelectedDepartureId={setSelectedDepartureId}
                                departureStops={departureStops}
                                availableRouteStops={availableRouteStops}
                                refreshDepartureStops={async () => {
                                    if (selectedDepartureId) {
                                        await loadDepartureStops(selectedDepartureId);
                                        await loadRouteStopsForDeparture(selectedDepartureId);
                                    }
                                }}
                            />
                        </Tabs.Panel>
                    </Tabs>
                </Paper>
            </Container>
        </div>
    );
}